import React from 'react';
import { cn } from '../../utils/cn';

export type ContainerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Maximum width of the container
   * - sm: max-w-3xl (768px) - Ideal for forms, single-column content
   * - md: max-w-4xl (896px) - Ideal for blog posts, reading content
   * - lg: max-w-6xl (1152px) - Ideal for most page content
   * - xl: max-w-7xl (1280px) - Full width content (default)
   * - full: no max-width constraint
   */
  size?: ContainerSize;

  /**
   * Whether to use Tailwind's container class with responsive padding
   * When true, uses the container class from tailwind.config.js
   * When false, uses custom max-width with standard padding
   */
  useContainer?: boolean;

  /**
   * Padding size applied to the container
   * Only used when useContainer is false
   */
  padding?: 'none' | 'sm' | 'md' | 'lg';

  /**
   * HTML element to render as
   */
  as?: 'div' | 'section' | 'article' | 'main' | 'aside' | 'header' | 'footer';
}

const sizeClasses: Record<ContainerSize, string> = {
  sm: 'max-w-3xl',
  md: 'max-w-4xl',
  lg: 'max-w-6xl',
  xl: 'max-w-7xl',
  full: 'max-w-none',
};

const paddingClasses = {
  none: '',
  sm: 'px-4',
  md: 'px-4 sm:px-6',
  lg: 'px-4 sm:px-6 lg:px-8',
};

/**
 * Container component for consistent content width and centering
 *
 * @example
 * // Standard full-width container (uses Tailwind container)
 * <Container>Content</Container>
 *
 * // Narrow container for forms
 * <Container size="sm">Form content</Container>
 *
 * // Reading-optimized width
 * <Container size="md">Blog post content</Container>
 *
 * // Custom section
 * <Container as="section" className="py-16">
 *   Section content
 * </Container>
 */
export const Container = React.forwardRef<HTMLDivElement, ContainerProps>(
  (
    {
      children,
      className,
      size = 'xl',
      useContainer = false,
      padding = 'lg',
      as: Component = 'div',
      ...props
    },
    ref
  ) => {
    // When using Tailwind's container class
    if (useContainer) {
      return (
        <Component ref={ref} className={cn('container', className)} {...props}>
          {children}
        </Component>
      );
    }

    // Custom container with max-width
    return (
      <Component
        ref={ref}
        className={cn('mx-auto w-full', sizeClasses[size], paddingClasses[padding], className)}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

Container.displayName = 'Container';

/**
 * Section component - combines Container with vertical padding
 * Useful for page sections with consistent spacing
 */
export interface SectionProps extends ContainerProps {
  /**
   * Vertical padding size
   */
  spacing?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
}

const spacingClasses = {
  none: '',
  sm: 'py-8',
  md: 'py-12 md:py-16',
  lg: 'py-16 md:py-20 lg:py-24',
  xl: 'py-20 md:py-24 lg:py-32',
};

/**
 * Section wrapper with Container and vertical spacing
 *
 * @example
 * <Section spacing="lg">
 *   <h2>Section Title</h2>
 *   <p>Section content</p>
 * </Section>
 */
export const Section = React.forwardRef<HTMLDivElement, SectionProps>(
  ({ children, className, spacing = 'md', ...props }, ref) => {
    return (
      <Container
        ref={ref}
        as="section"
        className={cn(spacingClasses[spacing], className)}
        {...props}
      >
        {children}
      </Container>
    );
  }
);

Section.displayName = 'Section';
